/**
 * Site-logo click tracking. Kernel-owned: started by init alongside the page
 * view, so hosts get the event with zero page JS — the shell only has to mark
 * its logo with the `kz_site_logo` class.
 *
 * Yoto parity: `triggers/hooks.ts#trackLogoClickEvent` wires the same
 * `logo_click` event (consumer `LOGO_CLICK_EVENT`) on yoto-web's `.logo`
 * elements. Shells render their logo after content loads, so a static
 * querySelectorAll at init would find nothing — we delegate from the document
 * instead, which also covers re-renders and locale swaps without re-wiring.
 */

import { track } from './track';
import { CORE_EVENTS } from './coreEvents';

/** Marker class hosts put on the site/publisher logo element or its link. */
export const SITE_LOGO_CLASS = 'kz_site_logo';

let started = false;
let detach: (() => void) | null = null;

export function startLogoClickTracking(): void {
  if (started || typeof document === 'undefined') return;
  started = true;

  const onClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.' + SITE_LOGO_CLASS)) return;
    track(CORE_EVENTS.logoClick);
  };

  /* Capture phase: the event is recorded even when a handler on the logo
     stops propagation, and before a navigation tears the page down. */
  document.addEventListener('click', onClick, true);
  detach = () => document.removeEventListener('click', onClick, true);
}

/** Test-only — allow a fresh wiring cycle. */
export function _resetLogoClickTrackingForTests(): void {
  if (detach) detach();
  detach = null;
  started = false;
}
